import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GenAILiveClient } from "../../voice-chat/hooks/genai-live-client";
import { AudioStreamer } from "../../voice-chat/audio/audio-streamer";
// Remove AudioRecorder import since we're implementing direct recording
import { createBlob } from "../../voice-chat/utils/utils";
import VolMeterWorket from "../../voice-chat/audio/worklets/vol-meter-worklet";
import { LiveConnectConfig, Modality } from "@google/genai";
import { liveMessageDataHub } from "../data/liveMessageData/liveMessageDataHub";
import { ChatStatus } from "../data/globalChatState";
import { useLiveMessageActions } from "./useLiveMessages";
import { useAuth } from "@/app/user-and-session/session-auth/hooks/useAuth";
import { useLocale } from "@/app/user-and-session/languages/i18n/locale-provider";
import { parseDialogues } from "../frontend/shared/textUtil";
import { generateV4UUID } from "@/app/utils/uuid";
import { useUserAppData } from "@/app/user-and-session/user-app-data/useUserAppData";
import { CallPerMessagePrice, LiveChatPerMessagePrice } from "../data/chatSetting/settingOptions";
import { logMessageGenerationAction } from "../server/action/message-gen-log-actions";
import { useGlobalChatState, useGlobalLiveKitRoomState, useChatStatus } from "./useGlobalChatState";

export type UseGeminiLiveParams = {
  avatarName: string;
  characterPrompt: string;
  userPreferredName: string;
};

export type UseGeminiLiveResults = {
  connected: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  resetResumptionHandle: () => void;
  volume: number;
  muted: boolean;
  setMuted: (muted: boolean) => void;
  sendText: (text: string) => Promise<void>;
  sendInitSystemAudio: () => Promise<void>;
  tokenUsage: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
  // Streaming state for UI controls
  streamingUserMessageId: string | null;
  streamingAssistantMessageId: string | null;
  onGreetingTurn: boolean;
  sessionId: string | null;
  elapsedTime: number;
  isLowOnCredits: boolean;
};

/**
 * Hook for managing Gemini Live voice chat
 * Combines functionality from voice-chat's use-live-api and page config
 * Integrates with existing chat by fetching system prompt from backend API
 */
export function useGeminiLive({
  avatarName,
  characterPrompt,
  userPreferredName
}: UseGeminiLiveParams): UseGeminiLiveResults {
  const { locale } = useLocale();
  // Initialize client with empty API key (will be updated with ephemeral token)
  const client = useMemo(() => new GenAILiveClient({ apiKey: '' }), []);
  const audioStreamerRef = useRef<AudioStreamer | null>(null);

  // Direct audio recorder implementation (like audio-orb)
  const audioRecorderRef = useRef<{
    audioContext: AudioContext | undefined;
    inputNode: GainNode | undefined;
    sourceNode: MediaStreamAudioSourceNode | undefined;
    scriptProcessorNode: ScriptProcessorNode | undefined;
    mediaStream: MediaStream | undefined;
    isRecording: boolean;
  } | null>(null);

  // Message actions for syncing transcriptions
  const messageActions = useLiveMessageActions();
  const { userEmail, userName } = useAuth();
  const [userAppDataState, userAppDataActions] = useUserAppData(userEmail || '');
  
  // Get LiveKit room for lip sync audio publishing
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  const { chatStatus, setChatStatus } = useChatStatus();
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();

  const [connected, setConnected] = useState(false);
  const [volume, setVolume] = useState(0);
  const [muted, setMuted] = useState(true);
  const [inVolume, setInVolume] = useState(0);
  const [tokenUsage, setTokenUsage] = useState({
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
  });
  const tokenUsageRef = useRef({
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
  });
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const elapsedTimeRef = useRef(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isLowOnCredits, setIsLowOnCredits] = useState(false);
  const timerStartTimeRef = useRef<number | null>(null);
  // Ref to store latest ttsId from hook to avoid closure issues in callbacks
  const ttsIdRef = useRef<string>(globalChatStateData.userChatSetting.ttsId);
  
  // Keep ref in sync with hook value
  useEffect(() => {
    ttsIdRef.current = globalChatStateData.userChatSetting.ttsId;
  }, [globalChatStateData.userChatSetting.ttsId]);

  // Store config for connection
  const configRef = useRef<LiveConnectConfig | null>(null);

  // Local state for streaming message IDs (purely local, not synced to globalChatState)
  const streamingUserMessageIdRef = useRef<string | null>(null);
  const streamingAssistantMessageIdRef = useRef<string | null>(null);
  
  // Flag to track if we're in the initial greeting turn (after sending system audio)
  const onGreetingTurnRef = useRef<boolean>(false);

  // Audio buffering for lip sync (mimics Python tts_manager.py)
  const [isReceivingAudioData, setIsReceivingAudioData] = useState(false);
  const audioBufferRef = useRef<Uint8Array>(new Uint8Array(0));
  const firstBatchSentRef = useRef(false);
  const audioBufferTargetSize = 16000; // Target buffer size in bytes (matches Python)
  const rawBytesChannelName = "raw_bytes_llm_to_gpu"; // Channel name for audio data
  
  // Resampler for 24kHz -> 16kHz conversion (Web Audio API)
  const resamplerContextRef = useRef<AudioContext | null>(null);

  /**
   * Helper function: Resample audio from 24kHz to 16kHz
   * Prepare audio for audio lip sync
   */
  const resampleAudio = useCallback(async (audioData: Uint8Array, fromSampleRate: number, toSampleRate: number): Promise<Uint8Array> => {
    try {
      // Create audio context if needed
      if (!resamplerContextRef.current) {
        resamplerContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      
      const context = resamplerContextRef.current;
      
      // Convert Uint8Array (PCM16) to Float32Array for Web Audio API
      const int16Array = new Int16Array(audioData.buffer, audioData.byteOffset, audioData.byteLength / 2);
      const float32Array = new Float32Array(int16Array.length);
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / (int16Array[i] < 0 ? 0x8000 : 0x7FFF);
      }
      
      // Create audio buffer with source data
      const audioBuffer = context.createBuffer(1, float32Array.length, fromSampleRate);
      audioBuffer.copyToChannel(float32Array, 0);
      
      // Create offline context for resampling
      const ratio = toSampleRate / fromSampleRate;
      const outputLength = Math.floor(audioBuffer.length * ratio);
      const offlineContext = new OfflineAudioContext(1, outputLength, toSampleRate);
      
      // Create buffer source
      const source = offlineContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(offlineContext.destination);
      source.start(0);
      
      // Render resampled audio
      const renderedBuffer = await offlineContext.startRendering();
      const resampledFloat32 = renderedBuffer.getChannelData(0);
      
      // Convert back to PCM16 (Uint8Array)
      const resampledInt16 = new Int16Array(resampledFloat32.length);
      for (let i = 0; i < resampledFloat32.length; i++) {
        const sample = Math.max(-1, Math.min(1, resampledFloat32[i]));
        resampledInt16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      }
      
      return new Uint8Array(resampledInt16.buffer);
    } catch (error) {
      console.error("❌ Error resampling audio:", error);
      // Return original data as fallback
      return audioData;
    }
  }, []);

  /**
   * Helper function: Send raw audio bytes to LiveKit for lip sync
   * Send audio data to LiveKit for audio lip sync
   */
  const sendRawAudioBytes = useCallback(async (audioData: Uint8Array) => {
    if (chatStatus === ChatStatus.Call) {
      // Skip lip sync for CALL mode (audio-only, no video)
      return;
    }
    const room = globalLKRoomState.room;
    // Skip if room not connected or chat status is CALL (no lip sync needed for audio-only)
    if (!room || room.state !== 'connected') {
      console.warn("⚠️ Room not connected, skipping audio bytes transmission");
      return;
    }

    try {
      // Convert Uint8Array to base64 (matches Python's base64.b64encode)
      const base64Audio = btoa(String.fromCharCode(...audioData));
      
      // Create data packet matching Python's structure
      const dataPacket = {
        type: "raw_audio_bytes",
        channel: rawBytesChannelName,
        sample_rate: 16000,
        num_channels: 1,
        bytes_per_sample: 2, // int16
        frame_samples: audioData.length / 2, // 2 bytes per sample
        audio_data: base64Audio,
        timestamp: Date.now() / 1000 // Convert to seconds like Python's time.time()
      };

      // Send via LiveKit data channel
      await room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify(dataPacket)),
        { reliable: true }
      );

      console.log(`📤 Sent raw audio bytes: ${audioData.length} bytes via data channel`);
    } catch (error) {
      console.error("❌ Error sending raw audio bytes:", error);
    }
  }, [globalLKRoomState.room, rawBytesChannelName, chatStatus]);

  /**
   * Helper function: Send control flags to LiveKit for lip sync
   * Send control flags to LiveKit for audio lip sync
   */
  const sendRawFlag = useCallback(async (flag: string) => {
    if (chatStatus === ChatStatus.Call) {
      // Skip lip sync for CALL mode (audio-only, no video)
      return;
    }

    const room = globalLKRoomState.room;
    // Skip if room not connected or chat status is CALL (no lip sync needed for audio-only)
    if (!room || room.state !== 'connected') {
      console.warn("⚠️ Room not connected, skipping flag transmission:", flag);
      return;
    }
    
    try {
      // Create flag packet matching Python's structure
      const flagPacket = {
        type: "raw_tts_flag",
        channel: rawBytesChannelName,
        flag: flag,
        timestamp: Date.now() / 1000 // Convert to seconds like Python's time.time()
      };

      // Send via LiveKit data channel
      await room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify(flagPacket)),
        { reliable: true }
      );

      console.log(`🚩 Sent raw flag: ${flag} via data channel`);
    } catch (error) {
      console.error(`❌ Error sending raw flag ${flag}:`, error);
    }
  }, [globalLKRoomState.room, rawBytesChannelName, chatStatus]);

  /**
   * Helper function: Buffer audio or send directly
   * Buffer audio or send directly to LiveKit for audio lip sync
   */
  const bufferOrSendAudio = useCallback(async (audioData: Uint8Array) => {
    if (!firstBatchSentRef.current) {
      // Buffer initial audio until we reach target size
      const newBuffer = new Uint8Array(audioBufferRef.current.length + audioData.length);
      newBuffer.set(audioBufferRef.current);
      newBuffer.set(audioData, audioBufferRef.current.length);
      audioBufferRef.current = newBuffer;

      console.log(`🔄 Buffering audio: ${audioBufferRef.current.length}/${audioBufferTargetSize} bytes`);

      // Check if we've reached target buffer size
      if (audioBufferRef.current.length >= audioBufferTargetSize) {
        // Send START flag first
        await sendRawFlag("[START]");
        
        // Then send buffered data
        await sendRawAudioBytes(audioBufferRef.current);
        
        // Clear buffer and mark first batch as sent
        audioBufferRef.current = new Uint8Array(0);
        firstBatchSentRef.current = true;
        
        console.log("✅ Sent first buffered batch with START flag");
      }
    } else {
      // First batch already sent, send subsequent chunks directly
      await sendRawAudioBytes(audioData);
    }
  }, [sendRawAudioBytes, sendRawFlag, audioBufferTargetSize]);

  /**
   * Helper function: Flush any remaining buffered audio
   * Flush any remaining buffered audio to LiveKit for audio lip sync
   */
  const flushAudioBuffer = useCallback(async () => {
    if (audioBufferRef.current.length > 0 || !firstBatchSentRef.current) {
      // Send START flag if not sent yet
      if (!firstBatchSentRef.current) {
        await sendRawFlag("[START]");
      }
      
      // Send any remaining buffered data
      if (audioBufferRef.current.length > 0) {
        await sendRawAudioBytes(audioBufferRef.current);
        console.log(`🔄 Flushed remaining buffered data: ${audioBufferRef.current.length} bytes`);
      }
      
      // Clear buffer
      audioBufferRef.current = new Uint8Array(0);
      firstBatchSentRef.current = true;
    }
  }, [sendRawAudioBytes, sendRawFlag]);

  // Initialize audio contexts synchronously (iOS requirement - like audio-orb)
  useEffect(() => {
    const initAudio = () => {
      try {
        // Create output audio context synchronously (24kHz for playback)
        if (!audioStreamerRef.current) {
          const outputAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
            sampleRate: 24000
          });
          audioStreamerRef.current = new AudioStreamer(outputAudioContext);
          audioStreamerRef.current
            .addWorklet<any>("vumeter-out", VolMeterWorket, (ev: any) => {
              setVolume(ev.data.volume);
            })
            .then(() => {
              console.log("✅ Audio streamer worklet ready");
            });
        }

        // Create input audio context synchronously (16kHz for recording)
        if (!audioRecorderRef.current) {
          const inputAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
            sampleRate: 16000
          });
          const inputNode = inputAudioContext.createGain();
          audioRecorderRef.current = {
            audioContext: inputAudioContext,
            inputNode,
            sourceNode: undefined,
            scriptProcessorNode: undefined,
            mediaStream: undefined,
            isRecording: false
          };
        }

        console.log("✅ Audio contexts initialized synchronously");
      } catch (error) {
        console.error("❌ Failed to initialize audio contexts:", error);
      }
    };

    // Initialize immediately if possible, or wait for user interaction (iOS requirement)
    if (document.readyState === 'complete') {
      initAudio();
    } else {
      window.addEventListener('load', initAudio, { once: true });
    }

    // Also try on first user interaction as fallback
    const handleUserInteraction = () => {
      initAudio();
    };
    window.addEventListener('pointerdown', handleUserInteraction, { once: true });
    window.addEventListener('keydown', handleUserInteraction, { once: true });

    return () => {
      window.removeEventListener('pointerdown', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('load', initAudio);
    };
  }, []);

  // Timer effect to update elapsed time every second
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (isTimerRunning && timerStartTimeRef.current !== null) {
      const updateTimer = () => {
        const now = Date.now();
        const elapsed = Math.floor((now - timerStartTimeRef.current!) / 1000); // Convert to seconds
        setElapsedTime(elapsed);
        elapsedTimeRef.current = elapsed;
      };

      // Update immediately
      updateTimer();
      // Then set up interval
      intervalId = setInterval(updateTimer, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isTimerRunning]);

  // Setup client event listeners
  useEffect(() => {
    const onOpen = () => {
      console.log("🔗 Gemini Live connection opened");
      setConnected(true);
    };

    const onSetupComplete = async () => {
      console.log("🚀 Gemini Live setup complete");

      // Generate session ID and start timer
      const newSessionId = generateV4UUID();
      const currentTime = Date.now();
      setSessionId(newSessionId);
      sessionIdRef.current = newSessionId;
      timerStartTimeRef.current = currentTime;
      setElapsedTime(0); // Reset elapsed time
      elapsedTimeRef.current = 0;
      setIsTimerRunning(true); // Start the timer

      // Automatically send system audio to prompt the model to start conversation
      try {
        await sendInitSystemAudioInternal();
      } catch (error) {
        console.error("Failed to send initial system audio:", error);
      }
    };

    const onClose = () => {
      console.log("🔌 Gemini Live connection closed");
      setConnected(false);
      // Reset token usage when connection closes
      const resetUsage = {
        inputTokens: 0,
        outputTokens: 0,
        totalTokens: 0,
      };
      setTokenUsage(resetUsage);
      tokenUsageRef.current = resetUsage;

      // Reset session ID and timer when connection closes
      setSessionId(null);
      sessionIdRef.current = null;
      timerStartTimeRef.current = null;
      setElapsedTime(0);
      elapsedTimeRef.current = 0;
      setIsTimerRunning(false); // Stop the timer
      setIsLowOnCredits(false); // Reset low credit state

      // If we're still in Call status, flip back to INFO
      if (chatStatus === ChatStatus.Call || chatStatus === ChatStatus.LIVE) {
        setChatStatus(ChatStatus.INFO);
      }
    };

    const onError = (error: ErrorEvent) => {
      console.error("❌ Gemini Live error:", error);
    };

    const stopAudioStreamer = async () => {
      console.log("⏹️ Hook: Audio streamer stopped due to interruption");
      audioStreamerRef.current?.stop();
      
      // Flush any remaining buffered audio and send INTERRUPTED flag
      if (isReceivingAudioData) {
        await flushAudioBuffer();
        await sendRawFlag("[INTERRUPTED]");
        
        // Reset audio buffering state
        setIsReceivingAudioData(false);
        audioBufferRef.current = new Uint8Array(0);
        firstBatchSentRef.current = false;
        console.log("🔄 Reset audio buffering state after interruption");
      }
    };

    const onAudio = async (data: ArrayBuffer) => {
      console.log("🎙️ Hook: Audio data received, data length:", data.byteLength);
      
      // For LIVE mode (with video): Mute local audio output, LiveKit handles audio playback
      // For CALL mode (audio-only): Play audio locally through audio streamer
      if (chatStatus === ChatStatus.LIVE) {
        // Mute local audio output for LIVE mode - LiveKit room handles audio
        // Don't add to audio streamer
        console.log("🔇 LIVE mode: Skipping local audio playback (LiveKit handles audio)");
      } else {
        // CALL mode or other: Play audio locally
        audioStreamerRef.current?.addPCM16(new Uint8Array(data));
      }
      
      // Process audio for lip sync (only for LIVE mode with video)
      // Skip for CALL mode (audio-only, no lip sync needed)
      if (chatStatus === ChatStatus.Call) {
        // Skip lip sync processing for CALL mode
        return;
      }
      
      try {
        // Set receiving audio flag on first audio data
        if (!isReceivingAudioData) {
          setIsReceivingAudioData(true);
          // Reset buffer state for new audio stream
          audioBufferRef.current = new Uint8Array(0);
          firstBatchSentRef.current = false;
          console.log("🎙️ Started receiving audio data for lip sync");
        }
        
        // Gemini Live outputs 24kHz PCM16 audio, need to resample to 16kHz for lip sync
        const audioData = new Uint8Array(data);
        const resampledAudio = await resampleAudio(audioData, 24000, 16000);
        
        // Buffer or send audio data
        await bufferOrSendAudio(resampledAudio);
      } catch (error) {
        console.error("❌ Error processing audio for lip sync:", error);
      }
    };

    const onUsage = (usage: any) => {
      const processedUsage = {
        inputTokens: usage.promptTokenCount || usage.inputTokenCount || 0,
        outputTokens: usage.responseTokenCount || usage.outputTokenCount || 0,
        totalTokens: usage.totalTokenCount || ((usage.promptTokenCount || usage.inputTokenCount || 0) + (usage.responseTokenCount || usage.outputTokenCount || 0)),
      };
      console.log("📊 Hook: Usage metadata received:", usage);
      console.log("📊 Hook: Usage metadata processed:", processedUsage);
      // Update token usage when usage metadata is received
      setTokenUsage(processedUsage);
      tokenUsageRef.current = processedUsage;
    };

    const onUserTranscription = (transcription: { text: string; finished: boolean }) => {
      const status = transcription.finished ? "FINAL" : "INTERIM";
      console.log(`🎙️ Hook: User transcription [${status}]: "${transcription.text || ''}"`);
      
      // Skip user transcription handling during greeting turn
      if (onGreetingTurnRef.current) {
        console.log("🎙️ Skipping user transcription during greeting turn");
        return;
      }
      
      // Sync transcription to messages (similar to text streaming)
      if (!transcription.text || !userEmail) return;
      
      // Check if we have an existing streaming message
      if (streamingUserMessageIdRef.current === null) {
        // Create new message ID and add frontend-only message
        const messageId = `user_voice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        streamingUserMessageIdRef.current = messageId;
        
        messageActions.addMessageFrontendOnly({
          id: messageId,
          content: transcription.text,
          role: 'user',
          sender_id: userEmail,
          sender_name: userName ?? 'User',
          created_at: new Date().toISOString(),
          isStreaming: true,
        });
      } else {
        // Append to existing streaming message
        messageActions.appendMessage(streamingUserMessageIdRef.current, transcription.text);
      }
    };

    const onAssistantTranscription = (transcription: { text: string; finished: boolean }) => {
      const status = transcription.finished ? "FINAL" : "INTERIM";
      console.log(`🎙️ Hook: Assistant transcription [${status}]: "${transcription.text || ''}"`);
      
      // Sync transcription to messages (similar to text streaming)
      if (!transcription.text) return;
      
      // Check if we have an existing streaming message
      if (streamingAssistantMessageIdRef.current === null) {
        // Create new message ID and add frontend-only message
        const messageId = `assistant_voice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        streamingAssistantMessageIdRef.current = messageId;
        
        messageActions.addMessageFrontendOnly({
          id: messageId,
          content: transcription.text,
          role: 'assistant',
          sender_id: 'gemini_live',
          sender_name: avatarName,
          created_at: new Date().toISOString(),
          isStreaming: true,
        });
      } else {
        // Append to existing streaming message
        messageActions.appendMessage(streamingAssistantMessageIdRef.current, transcription.text);
      }
    };

    const onTurnComplete = async () => {
      console.log("✅ Hook: Turn complete received");
      let thisTurnIsGreetingTurn = false; // use locally later for logging

      // Flush any remaining buffered audio and send DONE flag
      if (isReceivingAudioData && chatStatus === ChatStatus.LIVE) {
        await flushAudioBuffer();
        await sendRawFlag("[DONE]");
        
        // Reset audio buffering state
        setIsReceivingAudioData(false);
        audioBufferRef.current = new Uint8Array(0);
        firstBatchSentRef.current = false;
        console.log("🔄 Reset audio buffering state after turn complete");
      }

      // Reset greeting turn flag if we were in greeting turn
      if (onGreetingTurnRef.current) {
        console.log("🎙️ Greeting turn complete, resetting flag");
        thisTurnIsGreetingTurn = true;
        onGreetingTurnRef.current = false;
        setMuted(true);
      }

      // Finalize streaming messages and sync to database
      // Check user message
      if (streamingUserMessageIdRef.current !== null) {
        const userMessage = messageActions.findMessage?.(streamingUserMessageIdRef.current);
        if (userMessage) {
          // Update streaming status to false in frontend
          messageActions.updateMessage(streamingUserMessageIdRef.current, undefined, undefined, false);

          // Sync to database with final content
          const finalUserMessage = {
            ...userMessage,
            isStreaming: false,
          };
          messageActions.addMessageDBOnly(finalUserMessage);
          console.log(`📝 Synced user voice message to DB: ${streamingUserMessageIdRef.current}`);

          // Log the user transcription message generation
          logMessageGenerationAction({
            msg_id: streamingUserMessageIdRef.current,
            user_email: userEmail || '',
            avatar_id: globalChatStateData.character?.metadata.avatar_id || '',
            chat_status: 'CALL',
            role: 'user',
            msg_source: 'user_transcription',
            content: userMessage.content,
            input_token: 0,
            output_token: 0,
            total_token: 0,
            gems_cost: 0,
            gems_remain: userAppDataState.creditCount + userAppDataState.dailyCreditCount,
            live_session_id: sessionIdRef.current || '',
            voice_id: ttsIdRef.current,
            seconds_since_start: elapsedTimeRef.current,
          });
        }

        // Reset user streaming message ID
        streamingUserMessageIdRef.current = null;
      }

        // Check assistant message
      if (streamingAssistantMessageIdRef.current !== null) {
        const assistantMessage = messageActions.findMessage?.(streamingAssistantMessageIdRef.current);
        if (assistantMessage) {
          // Update streaming status to false in frontend
          messageActions.updateMessage(streamingAssistantMessageIdRef.current, undefined, undefined, false);

          // Sync to database with final content
          const finalAssistantMessage = {
            ...assistantMessage,
            isStreaming: false,
          };
          messageActions.addMessageDBOnly(finalAssistantMessage);
          console.log(`📝 Synced assistant voice message to DB: ${streamingAssistantMessageIdRef.current}`);

          // Check and deduct gems for voice message, log the message generation
          try {
            let totalGems = userAppDataState.creditCount + userAppDataState.dailyCreditCount;

            // Deduct gems for this turn
            let currentMode = 'CALL';
            let currentPrice = CallPerMessagePrice;
            if  (chatStatus === ChatStatus.LIVE) {
              currentPrice = LiveChatPerMessagePrice;
              currentMode = 'LIVE';
            }
            const result = await userAppDataActions.subtractGems(currentPrice);
            if (result.success) {
            console.log(`💰 Deducted ${currentPrice} gems for voice message turn`);
            totalGems = result.newCount;
            } else {
            console.error("❌ Failed to deduct gems:", result.message);
            setIsLowOnCredits(true);
            return;
            }

            console.log("💰 This turn is greeting turn:", thisTurnIsGreetingTurn);
            console.log("💰 Token usage from ref:", tokenUsageRef.current);

            // log the assistant message generation
            logMessageGenerationAction({
            msg_id: streamingAssistantMessageIdRef.current,
            user_email: userEmail || '',
            avatar_id: globalChatStateData.character?.metadata.avatar_id || '',
            chat_status: currentMode,
            role: 'assistant',
            msg_source: thisTurnIsGreetingTurn ? 'greeting_transcription' : 'assistant_transcription',
            content: assistantMessage.content,
            input_token: tokenUsageRef.current.inputTokens,
            output_token: tokenUsageRef.current.outputTokens,
            total_token: tokenUsageRef.current.totalTokens,
            gems_cost: currentPrice,
            gems_remain: totalGems,
            live_session_id: sessionIdRef.current || '',
            voice_id: ttsIdRef.current,
            seconds_since_start: elapsedTimeRef.current,
            });

            // Check if user has enough gems for one more message after deduction
            console.log("💰 Remaining gems after deduction:", totalGems, "expect", currentPrice, "gems");
            if (totalGems < currentPrice) {
            console.log("⚠️ Low on gems - only enough for one more voice message, expect", currentPrice, "gems, but only have", totalGems, "gems");
            setIsLowOnCredits(true);
            }
          } catch (error) {
              console.error("❌ Error handling voice gems:", error);
              // Continue with the rest of the logic even if gem deduction fails
          }
        }
        // Reset assistant streaming message ID
        streamingAssistantMessageIdRef.current = null;
      }
    };

    const onLog = (log: any) => {
      // Track token usage from logs (fallback for serverContent usageMetadata)
      if (log.type === 'server.content' && log.message?.serverContent?.usageMetadata) {
        const usage = log.message.serverContent.usageMetadata;
        const processedUsage = {
          inputTokens: usage.inputTokenCount || 0,
          outputTokens: usage.outputTokenCount || 0,
          totalTokens: (usage.inputTokenCount || 0) + (usage.outputTokenCount || 0),
        };
        console.log("📊 Hook: Log-based usage metadata:", usage);
        console.log("📊 Hook: Log-based usage processed:", processedUsage);
        setTokenUsage(processedUsage);
        tokenUsageRef.current = processedUsage;
      }
    };

    client
      .on("error", onError)
      .on("open", onOpen)
      .on("close", onClose)
      .on("setupcomplete", onSetupComplete)
      .on("interrupted", stopAudioStreamer)
      .on("audio", onAudio)
      .on("usage", onUsage)
      .on("userTranscription", onUserTranscription)
      .on("assistantTranscription", onAssistantTranscription)
      .on("turncomplete", onTurnComplete)
      .on("log", onLog);

    return () => {
      client
        .off("error", onError)
        .off("open", onOpen)
        .off("close", onClose)
        .off("setupcomplete", onSetupComplete)
        .off("interrupted", stopAudioStreamer)
        .off("audio", onAudio)
        .off("usage", onUsage)
        .off("userTranscription", onUserTranscription)
        .off("assistantTranscription", onAssistantTranscription)
        .off("turncomplete", onTurnComplete)
        .off("log", onLog);
    };
  }, [
    client, 
    messageActions, 
    userEmail, 
    userName, 
    avatarName, 
    isReceivingAudioData, 
    resampleAudio, 
    bufferOrSendAudio, 
    flushAudioBuffer, 
    sendRawFlag,
    chatStatus
  ]);

  // Setup audio recorder events (direct approach like audio-orb)
  // Always keep MediaStream active when connected to maintain iOS speaker routing
  useEffect(() => {
    const recorder = audioRecorderRef.current;
    if (!recorder || !recorder.audioContext) return;

    const startRecordingSession = async () => {
      if (!recorder.audioContext) {
        throw new Error("Audio context not initialized");
      }

      try {
        // Resume audio context (critical for iOS)
        await recorder.audioContext.resume();

        // Request microphone access - keep this active even when muted
        recorder.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });

        // Create source from media stream
        recorder.sourceNode = recorder.audioContext.createMediaStreamSource(recorder.mediaStream);
        recorder.sourceNode.connect(recorder.inputNode!);

        // Create script processor for audio capture (like audio-orb)
        const bufferSize = 256;
        recorder.scriptProcessorNode = recorder.audioContext.createScriptProcessor(
          bufferSize,
          1,
          1
        );

        // Process audio data (like audio-orb)
        recorder.scriptProcessorNode.onaudioprocess = (audioProcessingEvent) => {
          const inputBuffer = audioProcessingEvent.inputBuffer;
          const pcmData = inputBuffer.getChannelData(0);

          // Always calculate volume (for UI feedback) but only send audio when not muted
          let sum = 0;
          for (let i = 0; i < pcmData.length; i++) {
            sum += pcmData[i] * pcmData[i];
          }
          const rms = Math.sqrt(sum / pcmData.length);
          setInVolume(recorder.isRecording ? rms : 0); // Set to 0 when "muted" for UI feedback

          // Only send audio data when recording is active (not muted)
          if (recorder.isRecording) {
            const blob = createBlob(pcmData);
            client.sendRealtimeInput([
              {
                mimeType: "audio/pcm;rate=16000",
                data: blob.data,
              },
            ]);
          }
        };

        // Connect nodes (like audio-orb)
        recorder.sourceNode.connect(recorder.scriptProcessorNode);
        recorder.scriptProcessorNode.connect(recorder.audioContext!.destination);

        console.log("🎙️ Recording session started (MediaStream active)");
      } catch (error) {
        console.error("❌ Failed to start recording session:", error);
        throw error;
      }
    };

    const stopRecordingSession = () => {
      // Disconnect and cleanup nodes (like audio-orb)
      if (recorder.scriptProcessorNode && recorder.sourceNode) {
        recorder.scriptProcessorNode.disconnect();
        recorder.sourceNode.disconnect();
      }

      recorder.scriptProcessorNode = undefined;
      recorder.sourceNode = undefined;

      // Stop media stream tracks
      if (recorder.mediaStream) {
        recorder.mediaStream.getTracks().forEach((track) => track.stop());
        recorder.mediaStream = undefined;
      }

      recorder.isRecording = false;
      console.log("⏹️ Recording session stopped");
    };

    // Only start MediaStream when connected - disconnect() handles stopping
    if (connected && recorder && !recorder.mediaStream) {
      console.log("🎙️ useEffect: Starting recording session (connected)");
      startRecordingSession();
    }
    // NOTE: We DON'T automatically stop when !connected - disconnect() handles that

    // Control recording state based on mute (but keep MediaStream active when connected)
    if (connected && !muted && recorder) {
      recorder.isRecording = true;
    } else if (recorder) {
      recorder.isRecording = false;
    }

    return () => {
      // Only cleanup on component unmount, not on disconnect
      // disconnect() handles its own cleanup
      if (recorder.mediaStream) {
        console.log("🧹 useEffect cleanup: Stopping recording session (component unmount)");
        stopRecordingSession();
      }
    };
  }, [connected, client, muted]);

  /**
   * Connect to Gemini Live with system prompt constructed from backend API
   */
  const connect = useCallback(async () => {
    try {
      console.log("🚀 Starting Gemini Live connection...");

      // 1. Get last 10 messages from chat history
      const messages = liveMessageDataHub.getMessages();
      const recentMessages = messages.slice(-10).map(msg => {
        // Parse dialogues from content and join them back into a string without spaces
        const dialogues = parseDialogues(msg.content);
        const processedContent = dialogues.length > 0 ? dialogues.join('') : msg.content;
        const procesWithQuotes = `"${processedContent}\n"`;

        return {
          role: msg.role,
          content: procesWithQuotes
        };
      });
      console.log(`📚 Including ${recentMessages.length} recent messages in system prompt`);

      // 2. Fetch system prompt from backend (includes chat history)
      const systemPromptResponse = await fetch('/api/chat/systemp_prompt/gemini_live', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          characterName: avatarName,
          characterPrompt,
          userPreferredName,
          conversationHistory: recentMessages,
          locale: locale,
        }),
      });

      if (!systemPromptResponse.ok) {
        throw new Error(`Failed to fetch system prompt: ${systemPromptResponse.status}`);
      }

      const { systemPrompt } = await systemPromptResponse.json();
      console.log("📝 System prompt received with conversation history:", systemPrompt);

      // CRITICAL: Read from ref to avoid closure-captured stale value from hook
      const ttsId = ttsIdRef.current;
      console.log("🔊 TTS ID:", ttsId);
      // 3. Construct config with system prompt (conversation history is now included in system prompt)
      const config: LiveConnectConfig = {
        responseModalities: [Modality.AUDIO],
        systemInstruction: systemPrompt,
        speechConfig: {
          voiceConfig: {
            prebuiltVoiceConfig: {
              voiceName: ttsId
            }
          }
        },
        contextWindowCompression: {
            slidingWindow: {
              targetTokens: "1600"
            },
            triggerTokens: "3200"
        },
        inputAudioTranscription: {},
        outputAudioTranscription: {},
      };

      configRef.current = config;

      // 4. Fetch ephemeral token from backend
      const tokenResponse = await fetch('/api/chat/gemini_auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!tokenResponse.ok) {
        throw new Error(`Failed to fetch ephemeral token: ${tokenResponse.status}`);
      }

      const tokenData = await tokenResponse.json();
      console.log("🔑 Ephemeral token received");

      // 5. Update client's API key with the ephemeral token
      client.updateApiKey(tokenData.token);

      // 6. Disconnect any existing connection and connect with new config
      client.disconnect();
      const model = "gemini-2.5-flash-native-audio-preview-09-2025";
      const resumptionHandle = client.resumptionHandle;
      await client.connect(model, config, resumptionHandle);

      console.log("✅ Successfully connected to Gemini Live");
    } catch (error) {
      console.error('❌ Error connecting to Gemini Live:', error);
      throw error;
    }
  }, [client, avatarName, characterPrompt, userPreferredName]);

  /**
   * Disconnect from Gemini Live
   */
  const disconnect = useCallback(async () => {
    console.log("🔌 Disconnecting from Gemini Live...");
    client.disconnect();
    setConnected(false);

    // Stop audio recorder session completely (direct approach)
    const recorder = audioRecorderRef.current;
    if (recorder) {
      console.log("🔴 Disconnect: Stopping audio recorder session...");
      recorder.isRecording = false;

      // CRITICAL: Disconnect nodes first
      if (recorder.scriptProcessorNode && recorder.sourceNode) {
        console.log("🔴 Disconnect: Disconnecting audio nodes...");
        recorder.scriptProcessorNode.disconnect();
        recorder.sourceNode.disconnect();
      }

      // Reset node references
      recorder.scriptProcessorNode = undefined;
      recorder.sourceNode = undefined;

      // CRITICAL: Stop MediaStream tracks (iOS specific)
      if (recorder.mediaStream) {
        console.log("🔴 Disconnect: Stopping MediaStream tracks...");
        const tracks = recorder.mediaStream.getTracks();
        console.log(`🔴 Disconnect: Found ${tracks.length} tracks to stop`);
        tracks.forEach((track, index) => {
          console.log(`🔴 Disconnect: Stopping track ${index}: ${track.kind}, enabled: ${track.enabled}, readyState: ${track.readyState}`);
          track.stop();
          console.log(`🔴 Disconnect: Track ${index} stopped, readyState now: ${track.readyState}`);
        });
        recorder.mediaStream = undefined;
        console.log("🔴 Disconnect: MediaStream cleared");
      }

      // CRITICAL: Suspend AudioContext on iOS to fully release microphone
      if (recorder.audioContext && recorder.audioContext.state !== 'closed') {
        try {
          console.log(`🔴 Disconnect: AudioContext state before suspend: ${recorder.audioContext.state}`);
          await recorder.audioContext.suspend();
          console.log(`🔴 Disconnect: AudioContext suspended, state now: ${recorder.audioContext.state}`);
        } catch (error) {
          console.error("🔴 Disconnect: Error suspending AudioContext:", error);
        }
      }

      console.log("⏹️ Recording session stopped on disconnect");
    }

    // Stop audio streamer
    if (audioStreamerRef.current) {
      console.log("🔴 Disconnect: Stopping audio streamer...");
      audioStreamerRef.current.stop();
    }

    // Clear streaming message IDs and reset greeting turn flag
    streamingUserMessageIdRef.current = null;
    streamingAssistantMessageIdRef.current = null;
    onGreetingTurnRef.current = false;

    // Reset audio buffering state
    setIsReceivingAudioData(false);
    audioBufferRef.current = new Uint8Array(0);
    firstBatchSentRef.current = false;
    console.log("🔄 Reset audio buffering state on disconnect");

    console.log("✅ Disconnect complete");
  }, [client]);

  /**
   * Reset resumption handle to start fresh sessions
   */
  const resetResumptionHandle = useCallback(() => {
    console.log("🔄 Resetting resumption handle for fresh session");
    client.resetResumptionHandle();
  }, [client]);

  /**
   * Internal function to send system audio
   * This is the core implementation used by both the public API and auto-trigger
   */
  const sendInitSystemAudioInternal = async () => {
    try {
      console.log("🔊 Loading system audio file...");
      
      // Fetch the WAV file from public directory
      const response = await fetch('/other_audio/system_audio.wav');
      if (!response.ok) {
        throw new Error(`Failed to load system audio: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      
      // Decode WAV file to get PCM data
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Convert to 16kHz mono PCM if needed
      const targetSampleRate = 16000;
      let pcmData: Float32Array;
      
      if (audioBuffer.sampleRate === targetSampleRate) {
        // Use first channel if already at correct sample rate
        pcmData = audioBuffer.getChannelData(0);
      } else {
        // Resample to 16kHz
        const ratio = audioBuffer.sampleRate / targetSampleRate;
        const newLength = Math.floor(audioBuffer.length / ratio);
        pcmData = new Float32Array(newLength);
        const channelData = audioBuffer.getChannelData(0);
        
        for (let i = 0; i < newLength; i++) {
          const srcIndex = Math.floor(i * ratio);
          pcmData[i] = channelData[srcIndex];
        }
      }
      
      // Convert Float32Array to Int16Array (PCM16)
      const pcm16 = new Int16Array(pcmData.length);
      for (let i = 0; i < pcmData.length; i++) {
        // Clamp to [-1, 1] and convert to 16-bit integer
        const sample = Math.max(-1, Math.min(1, pcmData[i]));
        pcm16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      }
      
      // Convert to base64
      const uint8Array = new Uint8Array(pcm16.buffer);
      const base64Audio = btoa(String.fromCharCode(...uint8Array));
      
      console.log("🔊 Sending system audio to Gemini Live...");
      
      // Set greeting turn flag to true before sending audio
      onGreetingTurnRef.current = true;
      console.log("🎙️ Set greeting turn flag to true");
      
      // Unmute the audio otherwise the model will not start the conversation
      setMuted(false);
      
      // Send audio via realtimeInput to prompt model response
      // This simulates user activity and prompts the model to continue
      client.sendRealtimeInput([
        {
          mimeType: "audio/pcm;rate=16000",
          data: base64Audio,
        },
      ]);
      
      console.log("✅ System audio sent successfully");
    } catch (error) {
      console.error("❌ Error sending system audio:", error);
      throw error;
    }
  };

  /**
   * Send text message through Gemini Live session
   * Creates a user message in the frontend immediately and sends text to API
   */
  const sendText = useCallback(async (text: string) => {
    if (!connected || !text.trim()) {
      console.warn("Cannot send text: not connected or empty text");
      return;
    }

    console.log("📝 Sending text through Gemini Live:", text);

    // Send text to Gemini Live using client.send
    // This sends via BidiGenerateContentClientContent which interrupts model generation
    client.send({ text }, true);

    // Immediately add user message to frontend and database
    if (userEmail) {
        const messageId = `user_text_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Use addMessage to handle both frontend and database sync
        messageActions.addMessage({
          id: messageId,
          content: text,
          role: 'user',
          sender_id: userEmail,
          sender_name: userName ?? 'User',
          created_at: new Date().toISOString(),
          isStreaming: false, // Text messages are complete immediately
        });
        
        let currentMode = 'CALL';
        if  (chatStatus === ChatStatus.LIVE) {
          currentMode = 'LIVE';
        }
        // Log the user-typed message generation
        logMessageGenerationAction({
          msg_id: messageId,
          user_email: userEmail,
          avatar_id: globalChatStateData.character?.metadata.avatar_id || '',
          chat_status: currentMode,
          role: 'user',
          msg_source: 'user_type',
          content: text,
          input_token: 0,
          output_token: 0,
          total_token: 0,
          gems_cost: 0,
          gems_remain: userAppDataState.creditCount + userAppDataState.dailyCreditCount,
          live_session_id: sessionIdRef.current || '',
          voice_id: ttsIdRef.current,
          seconds_since_start: elapsedTimeRef.current,
        });
      }

  }, [connected, client, messageActions, userEmail, userName, userAppDataState.creditCount, sessionId, elapsedTime]);

  /**
   * Public API to send system audio to prompt the model to continue the conversation
   * Loads and sends a pre-recorded audio file to simulate user activity
   */
  const sendInitSystemAudio = useCallback(async () => {
    if (!connected) {
      console.warn("Cannot send system audio: not connected");
      return;
    }

    await sendInitSystemAudioInternal();
  }, [connected]);

  return {
    connected,
    connect,
    disconnect,
    resetResumptionHandle,
    volume,
    muted,
    setMuted,
    sendText,
    sendInitSystemAudio,
    tokenUsage,
    streamingUserMessageId: streamingUserMessageIdRef.current,
    streamingAssistantMessageId: streamingAssistantMessageIdRef.current,
    onGreetingTurn: onGreetingTurnRef.current,
    sessionId,
    elapsedTime,
    isLowOnCredits,
  };
}
